import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormControl } from '@angular/forms';
import { TranslocoService } from '@jsverse/transloco';
import { UserRole } from '../../../core/models/entities/role';

/** Droit attribué à un rôle : booléen (oui/non) ou clé de traduction d'une nuance. */
type Permission = boolean | string;

/** Une action d'un module et les droits associés par rôle. */
interface PermissionAction {
  key: string;
  perms: Partial<Record<UserRole, Permission>>;
}

/** Un module du tableau : soit des droits globaux, soit une liste d'actions. */
interface PermissionModule {
  key: string;
  global?: Partial<Record<UserRole, Permission>>;
  actions?: PermissionAction[];
}

@Component({
  selector: 'app-permissions-page',
  template: `
    <div class="container relative pb-16">
      <h1 class="md:pt-8">{{ 'permissions.page-title' | transloco }}</h1>

      <div
        class="sticky top-14 mt-16 rounded-t-md border-x border-t border-secondary-900 bg-white p-4 px-4 lg:px-16 lg:pt-16"
        >
        <div class="px-8">
          <div
            class="grid grid-cols-2 items-center font-bold uppercase md:grid-cols-6"
            >
            <div>{{ 'permissions.column.role' | transloco }}</div>
            @for (role of roleColumns; track role) {
              <div class="hidden text-center md:block">{{
                'roles.' + role | transloco
              }}</div>
            }
            <div class="text-center md:hidden">
              <app-select-input
                [options]="rolesOptions"
                [control]="roleControl"
                [marginBottom]="false"
              ></app-select-input>
            </div>
          </div>
        </div>
      </div>

      <div
        class="space-y-4 rounded-b-md border-x border-b border-secondary-900 px-4 pb-4 lg:px-16 lg:pb-16"
        >
        @for (module of modules; track module.key; let i = $index) {
          <div class="space-y-4">
            <div class="rounded-full bg-light px-8 py-6" [class.mt-16]="i > 0">
              <div class="grid grid-cols-2 items-center md:grid-cols-6">
                <div class="font-bold">{{
                  'permissions.module.' + module.key | transloco
                }}</div>
                @if (moduleHasGlobalPermissions(module)) {
                  @for (role of roleColumns; track role) {
                    <div
                      [innerHtml]="content(module, role)"
                      class="hidden text-center md:block"
                    ></div>
                  }
                  <div
                    class="text-center md:hidden"
                    [innerHtml]="content(module, roleControl.value)"
                  ></div>
                }
              </div>
            </div>
            @for (action of module.actions; track action.key) {
              <div
                class="grid grid-cols-2 items-center px-8 py-4 md:grid-cols-6"
                >
                <div>{{ 'permissions.action.' + action.key | transloco }}</div>
                @for (role of roleColumns; track role) {
                  <div
                    [innerHtml]="actionContent(action, role)"
                    class="hidden text-center md:block"
                  ></div>
                }
                <div
                  class="text-center md:hidden"
                  [innerHtml]="actionContent(action, roleControl.value)"
                ></div>
              </div>
            }
          </div>
        }
      </div>
    </div>
    `,
  standalone: false,
})
export class PermissionsPageComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  private translocoService = inject(TranslocoService);

  UserRole = UserRole;
  // Ordre des colonnes de rôles affichées dans le tableau.
  roleColumns: UserRole[] = [
    UserRole.Member,
    UserRole.MemberPlus,
    UserRole.Finance,
    UserRole.Staff,
    UserRole.Admin,
  ];
  rolesOptions: { label: string; value: UserRole }[] = [];
  roleControl = new UntypedFormControl(UserRole.Member);

  // Les libellés des modules, actions et valeurs nuancées sont des clés de
  // traduction (permissions.module.*, permissions.action.*, permissions.value.*).
  modules: PermissionModule[] = [
    {
      key: 'users',
      actions: [
        {
          key: 'view-list',
          perms: {
            [UserRole.Member]: true,
            [UserRole.MemberPlus]: true,
            [UserRole.Finance]: true,
            [UserRole.Staff]: true,
            [UserRole.Admin]: true,
          },
        },
        {
          key: 'add',
          perms: {
            [UserRole.Staff]: 'permissions.value.staff-or-member',
            [UserRole.Admin]: 'permissions.value.all-users',
          },
        },
        {
          key: 'edit',
          perms: {
            [UserRole.Member]: 'permissions.value.self',
            [UserRole.MemberPlus]: 'permissions.value.self',
            [UserRole.Finance]: 'permissions.value.self',
            [UserRole.Staff]: 'permissions.value.staff-or-member',
            [UserRole.Admin]: 'permissions.value.all-users',
          },
        },
        {
          key: 'delete',
          perms: {
            [UserRole.Staff]: 'permissions.value.staff-or-member',
            [UserRole.Admin]: 'permissions.value.all-users',
          },
        },
      ],
    },
    {
      key: 'rooms',
      global: { [UserRole.Admin]: true },
    },
    {
      key: 'bookings',
      actions: [
        {
          key: 'view-list',
          perms: {
            [UserRole.Member]: true,
            [UserRole.MemberPlus]: true,
            [UserRole.Finance]: true,
            [UserRole.Staff]: true,
            [UserRole.Admin]: true,
          },
        },
        {
          key: 'add-internal',
          perms: {
            [UserRole.Member]: true,
            [UserRole.MemberPlus]: true,
            [UserRole.Finance]: true,
            [UserRole.Staff]: true,
            [UserRole.Admin]: true,
          },
        },
        {
          key: 'add-external',
          perms: {
            [UserRole.MemberPlus]: true,
            [UserRole.Staff]: true,
            [UserRole.Admin]: true,
          },
        },
        {
          key: 'edit',
          perms: {
            [UserRole.Member]: 'permissions.value.own-bookings',
            [UserRole.MemberPlus]: 'permissions.value.all-bookings',
            [UserRole.Finance]: 'permissions.value.own-bookings',
            [UserRole.Staff]: 'permissions.value.all-bookings',
            [UserRole.Admin]: 'permissions.value.all-bookings',
          },
        },
        {
          key: 'cancel',
          perms: {
            [UserRole.Member]: 'permissions.value.own-bookings',
            [UserRole.MemberPlus]: 'permissions.value.all-bookings',
            [UserRole.Finance]: 'permissions.value.own-bookings',
            [UserRole.Staff]: 'permissions.value.all-bookings',
            [UserRole.Admin]: 'permissions.value.all-bookings',
          },
        },
        {
          key: 'export',
          perms: {
            [UserRole.Member]: false,
            [UserRole.MemberPlus]: false,
            [UserRole.Finance]: false,
            [UserRole.Staff]: true,
            [UserRole.Admin]: true,
          },
        },
      ],
    },
    {
      key: 'settings',
      global: {
        [UserRole.Member]: false,
        [UserRole.MemberPlus]: false,
        [UserRole.Finance]: false,
        [UserRole.Staff]: true,
        [UserRole.Admin]: true,
      },
    },
    {
      key: 'billing',
      global: {
        [UserRole.Finance]: true,
        [UserRole.Staff]: true,
        [UserRole.Admin]: true,
      },
    },
    {
      key: 'coupons',
      actions: [
        {
          key: 'view-list',
          perms: { [UserRole.Staff]: true, [UserRole.Admin]: true },
        },
        {
          key: 'add',
          perms: { [UserRole.Staff]: true, [UserRole.Admin]: true },
        },
        {
          key: 'toggle',
          perms: { [UserRole.Staff]: true, [UserRole.Admin]: true },
        },
        {
          key: 'delete',
          perms: { [UserRole.Staff]: true, [UserRole.Admin]: true },
        },
      ],
    },
    {
      key: 'account',
      actions: [
        {
          key: 'my-profile',
          perms: {
            [UserRole.Member]: true,
            [UserRole.MemberPlus]: true,
            [UserRole.Finance]: true,
            [UserRole.Staff]: true,
            [UserRole.Admin]: true,
          },
        },
        { key: 'host-data', perms: { [UserRole.Admin]: true } },
        { key: 'subscription', perms: { [UserRole.Admin]: true } },
        { key: 'security', perms: { [UserRole.Admin]: true } },
      ],
    },
  ];

  ngOnInit(): void {
    // Les options du sélecteur de rôle (vue mobile) sont reconstruites dès que
    // les traductions sont chargées et à chaque changement de langue.
    this.translocoService
      .selectTranslation()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.rolesOptions = this.roleColumns.map((role) => ({
          label: this.translocoService.translate('roles.' + role),
          value: role,
        }));
      });
  }

  actionContent(action: PermissionAction, userRole: UserRole): string {
    return this.render(action.perms[userRole]);
  }

  content(module: PermissionModule, userRole: UserRole): string {
    return this.render(module.global?.[userRole]);
  }

  moduleHasGlobalPermissions(module: PermissionModule): boolean {
    return (
      !!module.global?.[UserRole.Member] ||
      !!module.global?.[UserRole.Staff] ||
      !!module.global?.[UserRole.Admin]
    );
  }

  /** Transforme un droit en HTML : icône de validation, nuance traduite ou tiret. */
  private render(permission: Permission | undefined): string {
    if (permission === true) {
      return '<i class="fa-solid fa-circle-check text-primary-500"></i>';
    }
    if (typeof permission === 'string') {
      return this.translocoService.translate(permission);
    }
    return '-';
  }
}
